import { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw,
  Search,
  Settings,
  Edit2,
  TrendingDown,
  AlertTriangle,
  Printer
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { StockWithDetails, Location } from '../types';
import Modal from './Modal';
import { EmptyStateNoResults, EmptyStateNoData } from './EmptyState';
import LoadingState from './LoadingState';

// Función helper para obtener el color del stock según el mínimo
const getStockColor = (stock: number, minStock: number): string => {
  if (minStock === 0) return 'var(--text-primary)';
  if (stock < minStock) {
    return 'var(--error)';
  } else if (stock <= minStock * 1.2) {
    return 'var(--warning)';
  } else {
    return 'var(--success)';
  }
};

// Función helper para verificar si mostrar icono de alerta
const shouldShowAlert = (stock: number, minStock: number): boolean => {
  if (minStock === 0) return false;
  return stock < minStock * 0.5;
};

export default function StockStones() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { isAdmin } = useAuth();
  const [stoneStock, setStoneStock] = useState<StockWithDetails[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [multiplier, setMultiplier] = useState<number>(1);
  const [filters] = useState({});
  const [selectedStoneTypes, setSelectedStoneTypes] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('stock_stone_filter_types');
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch (e) {
        return new Set();
      }
    }
    return new Set();
  });
  const [searchStone, setSearchStone] = useState('');

  // Función helper para determinar el tipo de piedra basándose en el nombre/código
  const getStoneType = (nombre: string, codigo: string): 'BEAD' | 'DISC' | 'PIPE' | 'SPACER' | null => {
    const text = (nombre + ' ' + codigo).toUpperCase();
    if (text.includes('BEAD')) return 'BEAD';
    if (text.includes('DISC')) return 'DISC';
    if (text.includes('PIPE')) return 'PIPE';
    if (text.includes('SPACER')) return 'SPACER';
    return null;
  };

  const toggleStoneType = (type: string) => {
    const newTypes = new Set(selectedStoneTypes);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    setSelectedStoneTypes(newTypes);
    localStorage.setItem('stock_stone_filter_types', JSON.stringify(Array.from(newTypes)));
  };

  // Columnas visibles para piedras (se actualizará dinámicamente con los tamaños)
  const [visibleStoneColumns, setVisibleStoneColumns] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('stock_stone_columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        // Si hay error, usar valores por defecto
      }
    }
    // Valores por defecto: todas las columnas visibles
    return {
      codigo: true,
      nombre: true,
    };
  });

  const [isStoneColumnsModalOpen, setIsStoneColumnsModalOpen] = useState(false);
  const [isEditStockModalOpen, setIsEditStockModalOpen] = useState(false);
  const [isEditMinStockModalOpen, setIsEditMinStockModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [editingStock, setEditingStock] = useState<{
    stoneId: number;
    stoneCode: string;
    stoneName: string;
    locationId: number;
    locationName: string;
    currentQty: number;
  } | null>(null);
  const [editingMinStock, setEditingMinStock] = useState<{
    stoneId: number;
    stoneCode: string;
    stoneName: string;
    currentMin: number;
  } | null>(null);
  const [editStockForm, setEditStockForm] = useState({ new_qty: '' });
  const [editMinStockForm, setEditMinStockForm] = useState({ new_min: '' });

  // Escuchar eventos de búsqueda global
  useEffect(() => {
    const handleGlobalSearch = (e: CustomEvent) => {
      const { type, query } = e.detail;
      if (type === 'stone') {
        setSearchStone(query);
      }
    };

    window.addEventListener('global-search-select' as any, handleGlobalSearch as any);
    return () => {
      window.removeEventListener('global-search-select' as any, handleGlobalSearch as any);
    };
  }, []);

  const handleToggleStoneColumn = (column: string) => {
    const newColumns = { ...visibleStoneColumns, [column]: !visibleStoneColumns[column] };
    setVisibleStoneColumns(newColumns);
    localStorage.setItem('stock_stone_columns', JSON.stringify(newColumns));
  };

  useEffect(() => {
    loadLocations();
    loadMultiplier();
    loadStoneStock();
  }, []);

  // Inicializar columnas de tamaño de piedras cuando se cargan los datos
  useEffect(() => {
    if (stoneStock.length > 0) {
      const allSizes = new Set<number>();
      stoneStock.forEach((item) => {
        if (!item.stone) return;
        const size_mm_raw = item.stone.size_mm;
        let size_mm: number | null = size_mm_raw ? parseFloat(size_mm_raw.toString()) : null;
        if (!size_mm || isNaN(size_mm)) {
          const codMatch = item.stone.cod_piedra.match(/-(\d+)mm$/i);
          if (codMatch) {
            size_mm = parseInt(codMatch[1], 10);
          } else {
            const nombreMatch = item.stone.nombre_piedra.match(/\s+(\d+)mm$/i);
            if (nombreMatch) {
              size_mm = parseInt(nombreMatch[1], 10);
            }
          }
        }
        if (size_mm && !isNaN(size_mm) && size_mm > 0) {
          allSizes.add(Math.round(size_mm));
        }
      });

      const standardSizes = [4, 6, 8, 10, 12];
      const otherSizes = Array.from(allSizes).filter(s => !standardSizes.includes(s)).sort((a, b) => a - b);
      const allSizesArray = [...standardSizes, ...otherSizes];

      // Inicializar columnas de tamaños si no existen
      let updated = false;
      const newColumns = { ...visibleStoneColumns };
      allSizesArray.forEach(size => {
        const sizeKey = `size_${size}`;
        if (newColumns[sizeKey] === undefined) {
          newColumns[sizeKey] = true;
          updated = true;
        }
      });
      if (newColumns.sin_tamano === undefined) {
        newColumns.sin_tamano = true;
        updated = true;
      }

      if (updated) {
        setVisibleStoneColumns(newColumns);
        localStorage.setItem('stock_stone_columns', JSON.stringify(newColumns));
      }
    }
  }, [stoneStock]);

  const loadLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (err: any) {
      showError('Error al cargar ubicaciones');
    }
  };

  const loadMultiplier = async () => {
    try {
      const response = await api.get('/settings/min-stock-multiplier');
      // Bug fix: El backend devuelve { multiplier }, no { value }
      setMultiplier(parseFloat(response.data.multiplier) || 1);
    } catch (err: any) {
      if (import.meta.env.DEV) {
        console.error('Error al cargar multiplicador:', err);
      }
    }
  };

  const loadStoneStock = async () => {
    try {
      setLoading(true);
      // Cargar stock de piedras (solo activas)
      const stoneParams: any = { item_type: 'STONE' };
      const stoneResponse = await api.get('/stock', { params: stoneParams });
      const stoneStockData = stoneResponse.data;

      // Filtrar solo piedras activas (el backend ya filtra por is_active cuando se solicita)
      // Pero por si acaso, verificamos que exista el stone
      const activeStoneStock = stoneStockData.filter((stock: StockWithDetails) => {
        return stock.stone !== null && stock.stone !== undefined;
      });

      // Cargar mínimos de stock para piedras
      const stoneMinStockResponse = await api.get('/min-stock', { params: { item_type: 'STONE' } });
      const stoneMinStocks = stoneMinStockResponse.data;

      // Agregar información de mínimo a cada stock de piedra
      const stoneStockWithMin = activeStoneStock.map((stock: StockWithDetails) => {
        const minStock = stoneMinStocks.find(
          (m: any) => m.item_id === stock.item_id && m.location_id === null
        );
        const effectiveMin = minStock ? Math.round(minStock.min_qty_base * multiplier) : 0;
        return {
          ...stock,
          min_qty: effectiveMin,
        };
      });

      setStoneStock(stoneStockWithMin);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar stock de piedras');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStoneStock = (
    stoneId: number,
    stoneCode: string,
    stoneName: string,
    currentQty: number
  ) => {
    // Para piedras, la ubicación es siempre ALMACEN_PIEDRAS
    const almacenPiedras = locations.find((l: Location) => l.code === 'ALMACEN_PIEDRAS');
    if (!almacenPiedras) {
      showError('No se encontró la ubicación ALMACEN_PIEDRAS');
      return;
    }

    setEditingStock({
      stoneId,
      stoneCode,
      stoneName,
      locationId: almacenPiedras.id,
      locationName: almacenPiedras.nombre,
      currentQty,
    });
    setEditStockForm({ new_qty: currentQty.toString() });
    setIsEditStockModalOpen(true);
  };

  const handleEditStoneMinStock = (
    stoneId: number,
    stoneCode: string,
    stoneName: string,
    currentMin: number
  ) => {
    setEditingMinStock({
      stoneId,
      stoneCode,
      stoneName,
      currentMin,
    });
    setEditMinStockForm({ new_min: currentMin.toString() });
    setIsEditMinStockModalOpen(true);
  };

  const handleSaveStock = async () => {
    if (!editingStock) return;

    try {
      const newQty = parseFloat(editStockForm.new_qty);
      if (isNaN(newQty) || newQty < 0) {
        showError('La cantidad debe ser un número mayor o igual a 0');
        return;
      }

      const response = await api.post('/settings/adjust-stock', {
        item_type: 'STONE',
        item_id: editingStock.stoneId,
        location_id: editingStock.locationId,
        new_qty: newQty,
      });

      showSuccess(`Stock ajustado correctamente. Anterior: ${response.data.previous_qty}, Nuevo: ${response.data.new_qty}`);
      setIsEditStockModalOpen(false);
      setEditingStock(null);
      setEditStockForm({ new_qty: '' });

      await loadStoneStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al ajustar stock');
    }
  };

  const handleSaveMinStock = async () => {
    if (!editingMinStock) return;

    try {
      const newMin = parseFloat(editMinStockForm.new_min);
      if (isNaN(newMin) || newMin < 0) {
        showError('El mínimo debe ser un número mayor o igual a 0');
        return;
      }

      await api.post('/settings/adjust-min-stock', {
        item_type: 'STONE',
        item_id: editingMinStock.stoneId,
        location_id: null, // Mínimo global para piedras
        min_qty: newMin,
      });

      showSuccess(`Mínimo actualizado correctamente. Nuevo valor: ${newMin}`);
      setIsEditMinStockModalOpen(false);
      setEditingMinStock(null);
      setEditMinStockForm({ new_min: '' });
      await loadStoneStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al ajustar mínimo');
    }
  };

  // Función para imprimir la lista de stock de piedras
  const handlePrintStock = async (includeQuantities: boolean) => {
    try {
      setIsPrintModalOpen(false);
      showSuccess('Cargando datos para imprimir...');

      // Recalcular los grupos de piedras (similar a como se hace en el render)
      const stoneGroups = new Map<string, {
        cod_piedra: string;
        nombre_piedra: string;
        sizes: Map<number, { qty: number; min_qty: number }>;
      }>();

      stoneStock.forEach((item) => {
        if (!item.stone) return;

        const cod_piedra = item.stone.cod_piedra;
        const nombre_piedra = item.stone.nombre_piedra;
        const size_mm_raw = item.stone.size_mm;
        let size_mm: number | null = size_mm_raw ? parseFloat(size_mm_raw.toString()) : null;

        if (!size_mm || isNaN(size_mm)) {
          const codMatch = cod_piedra.match(/-(\d+)mm$/i);
          if (codMatch) {
            size_mm = parseInt(codMatch[1], 10);
          } else {
            const nombreMatch = nombre_piedra.match(/\s+(\d+)mm$/i);
            if (nombreMatch) {
              size_mm = parseInt(nombreMatch[1], 10);
            }
          }
        }

        const effectiveSize = (size_mm && !isNaN(size_mm) && size_mm > 0) ? Math.round(size_mm) : 0;
        let baseCodPiedra = cod_piedra.replace(/-\d+mm$/i, '');
        let baseNombrePiedra = nombre_piedra.replace(/\s+\d+mm$/i, '');

        const key = `${baseCodPiedra}-${baseNombrePiedra}`;
        if (!stoneGroups.has(key)) {
          stoneGroups.set(key, {
            cod_piedra: baseCodPiedra,
            nombre_piedra: baseNombrePiedra,
            sizes: new Map(),
          });
        }

        const group = stoneGroups.get(key)!;
        const currentQty = group.sizes.get(effectiveSize)?.qty || 0;
        const currentMin = group.sizes.get(effectiveSize)?.min_qty || 0;
        const itemMinQty = (item as any).min_qty || 0;

        group.sizes.set(effectiveSize, {
          qty: currentQty + item.qty,
          min_qty: Math.max(currentMin, itemMinQty),
        });
      });

      const stoneGroupsArray = Array.from(stoneGroups.values());

      // Filtrar por búsqueda y tipo de piedra
      const allGroups = stoneGroupsArray.filter((group) => {
        if (selectedStoneTypes.size > 0) {
          const stoneType = getStoneType(group.nombre_piedra, group.cod_piedra);
          if (!stoneType || !selectedStoneTypes.has(stoneType)) {
            return false;
          }
        }
        if (!searchStone.trim()) return true;
        const search = searchStone.toLowerCase().trim();
        return (
          group.cod_piedra.toLowerCase().includes(search) ||
          group.nombre_piedra.toLowerCase().includes(search)
        );
      });

      if (allGroups.length === 0) {
        showError('No hay piedras para imprimir');
        return;
      }

      // Generar HTML para imprimir
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showError('No se pudo abrir la ventana de impresión. Por favor, permite ventanas emergentes.');
        return;
      }

      // Calcular tamaños disponibles
      const allSizesSet = new Set<number>();
      stoneStock.forEach((item) => {
        if (!item.stone) return;
        const size_mm = item.stone.size_mm ? parseFloat(item.stone.size_mm.toString()) : null;
        if (size_mm && !isNaN(size_mm) && size_mm > 0) {
          allSizesSet.add(size_mm);
        }
      });
      const standardSizes = [4, 6, 8, 10, 12];
      const otherSizes = Array.from(allSizesSet).filter(s => !standardSizes.includes(s)).sort((a, b) => a - b);
      const allSizesArray = [...standardSizes, ...otherSizes];

      const hasNoSizeStones = stoneStock.some(item => {
        if (!item.stone) return false;
        const size_mm = item.stone.size_mm ? parseFloat(item.stone.size_mm.toString()) : null;
        return !size_mm || isNaN(size_mm) || size_mm === 0;
      });

      const sizesForPrint = hasNoSizeStones && !allSizesArray.includes(0)
        ? [0, ...allSizesArray]
        : allSizesArray;

      // Obtener tamaños visibles
      const visibleSizes = sizesForPrint.filter(size => {
        const sizeKey = size === 0 ? 'sin_tamano' : `size_${size}`;
        return visibleStoneColumns[sizeKey] !== false;
      });

      // Construir encabezados de columnas
      const columnHeaders: string[] = [];
      if (visibleStoneColumns.codigo) columnHeaders.push('CÓDIGO');
      if (visibleStoneColumns.nombre) columnHeaders.push('NOMBRE');
      visibleSizes.forEach(size => {
        const label = size === 0 ? 'Sin tamaño' : `${size}mm`;
        columnHeaders.push(label);
      });

      const getStockValue = (group: typeof filteredStones[0], size: number): string => {
        if (!includeQuantities) return '';

        const stockData = group.sizes.get(size);
        if (!stockData) return '';

        if (stockData.qty === 0 && stockData.min_qty === 0) {
          return '0 (s/m)';
        }

        const minText = stockData.min_qty > 0 ? ' (' + stockData.min_qty + ')' : '';
        return stockData.qty.toString() + minText;
      };

      const selectedTypesLabel = selectedStoneTypes.size > 0
        ? Array.from(selectedStoneTypes).join(', ')
        : 'Todas';

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Stock Piedras - ${includeQuantities ? 'Con Cantidades' : 'Plantilla Vacía'}</title>
          <style>
            @page {
              size: A4;
              margin: 1cm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 9px;
              margin: 0;
              padding: 0;
            }
            h1 {
              text-align: center;
              font-size: 16px;
              margin-bottom: 8px;
            }
            .info {
              text-align: center;
              font-size: 8px;
              color: #666;
              margin-bottom: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
              margin-top: 8px;
            }
            th {
              background-color: #f0f0f0;
              border: 1px solid #000;
              padding: 5px 3px;
              text-align: center;
              font-weight: bold;
              font-size: 8px;
            }
            td {
              border: 1px solid #000;
              padding: 4px 3px;
              text-align: left;
              font-size: 8px;
            }
            td:first-child,
            td:nth-child(2) {
              font-weight: bold;
            }
            td[data-align="center"] {
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @media print {
              body {
                margin: 0;
              }
              table {
                page-break-inside: auto;
              }
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
              thead {
                display: table-header-group;
              }
              tfoot {
                display: table-footer-group;
              }
            }
          </style>
        </head>
        <body>
          <h1>STOCK DE PIEDRAS</h1>
          <div class="info">
            ${includeQuantities ? 'Con Cantidades' : 'Plantilla Vacía'} | 
            Tipos: ${selectedTypesLabel} | 
            Fecha: ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })} | 
            Total de piedras: ${allGroups.length}
          </div>
          <table>
            <thead>
              <tr>
                ${columnHeaders.map(h => '<th>' + h + '</th>').join('')}
              </tr>
            </thead>
            <tbody>
              ${allGroups.map(group => {
        const row = [];
        if (visibleStoneColumns.codigo) row.push('<td><strong>' + group.cod_piedra + '</strong></td>');
        if (visibleStoneColumns.nombre) row.push('<td>' + group.nombre_piedra + '</td>');
        visibleSizes.forEach(size => {
          row.push('<td data-align="center">' + getStockValue(group, size) + '</td>');
        });
        return '<tr>' + row.join('') + '</tr>';
      }).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();

      // Esperar a que se cargue el contenido y luego imprimir
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };

      showSuccess('Ventana de impresión abierta');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al imprimir');
    }
  };

  // Componente para mostrar stock de piedras con edición
  // NOTA: Este componente NO puede usar hooks porque se renderiza condicionalmente dentro de un map
  // Usamos CSS puro con :hover en lugar de useState para evitar violar las reglas de los hooks
  const StoneStockDisplay = ({
    stock,
    minStock,
    stoneId,
    stoneCode,
    stoneName,
  }: {
    stock: number;
    minStock: number;
    stoneId: number;
    stoneCode: string;
    stoneName: string;
  }) => {
    const iconButtonStyle: React.CSSProperties = {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '0.1rem',
      lineHeight: 1,
      fontSize: '0.9rem',
    };

    // Generar un ID único para este componente para usar con CSS
    const hoverId = `stone-stock-${stoneId}-${stoneCode}`.replace(/[^a-zA-Z0-9-]/g, '-');

    return (
      <>
        <style>{`
          #${hoverId}:hover {
            background-color: var(--bg-secondary) !important;
          }
          #${hoverId}:hover .stone-stock-actions {
            opacity: 1 !important;
            pointer-events: auto !important;
          }
        `}</style>
        <span
          id={hoverId}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            position: 'relative',
            padding: '0.25rem 2rem 0.25rem 0.5rem',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s',
            justifyContent: 'center',
            minWidth: '80px',
          }}
          onClick={() => handleEditStoneStock(stoneId, stoneCode, stoneName, stock)}
          title="Clic para editar stock"
        >
          <strong style={{ color: getStockColor(stock, minStock) }}>
            {stock}
          </strong>
          {shouldShowAlert(stock, minStock) && (
            <span style={{ marginLeft: '0.25rem', display: 'flex', alignItems: 'center' }} title="Stock crítico: menos del 50% del mínimo">
              <AlertTriangle size={16} color="var(--error)" />
            </span>
          )}
          {minStock > 0 && (
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginLeft: '0.25rem' }}>
              ({minStock})
            </span>
          )}
          {stock === 0 && minStock === 0 && (
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginLeft: '0.25rem' }}>
              (s/m)
            </span>
          )}
          <span
            className="stone-stock-actions"
            style={{
              position: 'absolute',
              right: '0.35rem',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '0.25rem',
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 0.2s',
            }}
          >
            <button
              style={iconButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                handleEditStoneStock(stoneId, stoneCode, stoneName, stock);
              }}
              title="Editar stock"
            >
              <Edit2 size={14} />
            </button>
            <button
              style={iconButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                handleEditStoneMinStock(stoneId, stoneCode, stoneName, minStock);
              }}
              title="Editar mínimo"
            >
              <TrendingDown size={14} />
            </button>
          </span>
        </span>
      </>
    );
  };

  // Calcular grupos de piedras y tamaños - TODO debe estar en useMemo antes de cualquier early return
  // para evitar que el número de hooks cambie entre renders
  const { stoneGroupsArray, sizes } = useMemo(() => {
    // Agrupar piedras por código y nombre, mostrando stock por tamaño
    const stoneGroups = new Map<string, {
      cod_piedra: string;
      nombre_piedra: string;
      sizes: Map<number, { qty: number; min_qty: number }>;
    }>();

    // Primero, recopilar todos los tamaños únicos que existen en los datos
    const allSizes = new Set<number>();

    stoneStock.forEach((item) => {
      if (!item.stone) return;

      const size_mm = item.stone.size_mm ? parseFloat(item.stone.size_mm.toString()) : null;
      if (size_mm && !isNaN(size_mm) && size_mm > 0) {
        allSizes.add(size_mm);
      }
    });

    // Usar los tamaños estándar (4, 6, 8, 10, 12) y agregar cualquier otro tamaño encontrado
    const standardSizes = [4, 6, 8, 10, 12];
    const otherSizes = Array.from(allSizes).filter(s => !standardSizes.includes(s)).sort((a, b) => a - b);
    const calculatedSizes = [...standardSizes, ...otherSizes];

    // Si hay piedras sin tamaño (tamaño 0), agregar una columna especial al inicio
    const hasNoSizeStones = stoneStock.some(item => {
      if (!item.stone) return false;
      const size_mm = item.stone.size_mm ? parseFloat(item.stone.size_mm.toString()) : null;
      return !size_mm || isNaN(size_mm) || size_mm === 0;
    });

    const finalSizes = [...calculatedSizes];
    if (hasNoSizeStones && !finalSizes.includes(0)) {
      finalSizes.unshift(0); // Agregar al inicio
    }

    stoneStock.forEach((item) => {
      if (!item.stone) {
        return;
      }

      const cod_piedra = item.stone.cod_piedra;
      const nombre_piedra = item.stone.nombre_piedra;
      const size_mm_raw = item.stone.size_mm;

      // Intentar extraer el tamaño del código o nombre si no está en size_mm
      let size_mm: number | null = size_mm_raw ? parseFloat(size_mm_raw.toString()) : null;

      // Si no hay tamaño en size_mm, intentar extraerlo del código o nombre
      if (!size_mm || isNaN(size_mm)) {
        // Buscar patrón de tamaño en el código: -4mm, -6mm, etc.
        const codMatch = cod_piedra.match(/-(\d+)mm$/i);
        if (codMatch) {
          size_mm = parseInt(codMatch[1], 10);
        } else {
          // Buscar patrón de tamaño en el nombre: " 4mm", " 6mm", etc.
          const nombreMatch = nombre_piedra.match(/\s+(\d+)mm$/i);
          if (nombreMatch) {
            size_mm = parseInt(nombreMatch[1], 10);
          }
        }
      }

      // Redondear el tamaño al entero más cercano para que coincida con las columnas
      const effectiveSize = (size_mm && !isNaN(size_mm) && size_mm > 0) ? Math.round(size_mm) : 0;

      // Extraer el nombre base de la piedra (sin el tamaño)
      let baseCodPiedra = cod_piedra;
      let baseNombrePiedra = nombre_piedra;

      // Eliminar el patrón de tamaño del código (ej: -4mm, -6mm, etc.)
      baseCodPiedra = baseCodPiedra.replace(/-\d+mm$/i, '');

      // Eliminar el patrón de tamaño del nombre (ej: " 4mm", " 6mm", etc.)
      baseNombrePiedra = baseNombrePiedra.replace(/\s+\d+mm$/i, '');

      // Usar el nombre base como clave de agrupación
      const key = `${baseCodPiedra}-${baseNombrePiedra}`;
      if (!stoneGroups.has(key)) {
        stoneGroups.set(key, {
          cod_piedra: baseCodPiedra,
          nombre_piedra: baseNombrePiedra,
          sizes: new Map(),
        });
      }

      const group = stoneGroups.get(key)!;
      const currentQty = group.sizes.get(effectiveSize)?.qty || 0;
      const currentMin = group.sizes.get(effectiveSize)?.min_qty || 0;
      const itemMinQty = (item as any).min_qty || 0;

      // Sumar stock si hay múltiples registros para el mismo tamaño
      // Para el min_qty, usar el máximo entre el actual y el nuevo (en caso de múltiples registros)
      group.sizes.set(effectiveSize, {
        qty: currentQty + item.qty,
        min_qty: Math.max(currentMin, itemMinQty),
      });
    });

    return {
      stoneGroupsArray: Array.from(stoneGroups.values()),
      sizes: finalSizes,
    };
  }, [stoneStock]);

  // Filtrar por búsqueda y tipo de piedra
  // Optimizado con useMemo para evitar recálculos innecesarios
  const filteredStones = useMemo(() => {
    return stoneGroupsArray.filter((group) => {
      // Filtro por tipo de piedra
      if (selectedStoneTypes.size > 0) {
        const stoneType = getStoneType(group.nombre_piedra, group.cod_piedra);
        if (!stoneType || !selectedStoneTypes.has(stoneType)) {
          return false;
        }
      }

      if (!searchStone.trim()) return true;
      const search = searchStone.toLowerCase().trim();
      return (
        group.cod_piedra.toLowerCase().includes(search) ||
        group.nombre_piedra.toLowerCase().includes(search)
      );
    });
  }, [stoneGroupsArray, selectedStoneTypes, searchStone]);

  // Early return DESPUÉS de todos los hooks
  if (loading && stoneStock.length === 0) {
    return (
      <div className="tab-content">
        <LoadingState type="table" message="Cargando stock de piedras..." />
      </div>
    );
  }

  return (
    <div className="tab-content">
      {/* Filtros */}
      {/* Filtros por tipo de piedra */}
      <div className="table-container" style={{ marginBottom: '0.5rem', padding: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '300px' }}>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.125rem' }}>
              Filtrar por tipo de piedra:
            </label>
            <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
              {[
                { value: 'BEAD', label: 'Bead', color: '#3b82f6' },
                { value: 'DISC', label: 'Disc', color: '#a855f7' },
                { value: 'PIPE', label: 'Pipe', color: '#10b981' },
                { value: 'SPACER', label: 'Spacer', color: '#f59e0b' },
              ].map((type) => {
                const isSelected = selectedStoneTypes.has(type.value);
                return (
                  <div
                    key={type.value}
                    onClick={() => toggleStoneType(type.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? type.color : 'var(--border)'}`,
                      backgroundColor: isSelected ? `${type.color}15` : 'transparent',
                      color: isSelected ? type.color : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.2s ease',
                      fontSize: '0.85rem',
                      userSelect: 'none',
                      boxShadow: isSelected ? `0 2px 4px ${type.color}20` : 'none',
                    }}
                  >
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '3px',
                      border: `1.5px solid ${isSelected ? type.color : 'var(--text-secondary)'}`,
                      backgroundColor: isSelected ? type.color : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}>
                      {isSelected && (
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span>{type.label}</span>
                    {isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStoneType(type.value);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: type.color,
                          cursor: 'pointer',
                          borderRadius: '50%',
                          marginLeft: '2px',
                          opacity: 0.7,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            <input
              type="text"
              className="input"
              placeholder="Buscar por código o nombre..."
              value={searchStone}
              onChange={(e) => setSearchStone(e.target.value)}
              style={{ minWidth: '250px' }}
            />
            <button
              className="btn btn-secondary"
              onClick={() => setIsPrintModalOpen(true)}
              style={{ padding: '0.5rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              title="Imprimir lista de stock"
            >
              <Printer size={16} /> Imprimir
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setIsStoneColumnsModalOpen(true)}
              style={{ padding: '0.5rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              title="Personalizar columnas"
            >
              <Settings size={16} /> Columnas
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => loadStoneStock()}
              style={{ padding: '0.5rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              title="Actualizar stock"
            >
              <RefreshCw size={16} /> Actualizar
            </button>
          </div>
        </div>
      </div>


      {/* Tabla de Stock */}
      <div>
        {filteredStones.length === 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleStoneColumns.codigo && <th className="sticky-col">Código</th>}
                  {visibleStoneColumns.nombre && <th className="sticky-col">Nombre</th>}
                  {sizes.map(size => {
                    const sizeKey = size === 0 ? 'sin_tamano' : `size_${size}`;
                    const isVisible = visibleStoneColumns[sizeKey] !== false;
                    return isVisible ? (
                      <th key={size} style={{ textAlign: 'center' }}>
                        {size === 0 ? 'Sin tamaño' : `${size}mm`}
                      </th>
                    ) : null;
                  })}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={(() => {
                    let count = 0;
                    if (visibleStoneColumns.codigo) count++;
                    if (visibleStoneColumns.nombre) count++;
                    sizes.forEach(size => {
                      const sizeKey = size === 0 ? 'sin_tamano' : `size_${size}`;
                      if (visibleStoneColumns[sizeKey] !== false) count++;
                    });
                    return count || sizes.length + 2;
                  })()} className="text-center p-0">
                    {searchStone ? (
                      <EmptyStateNoResults onClearFilters={() => setSearchStone('')} />
                    ) : (
                      <EmptyStateNoData />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleStoneColumns.codigo && <th className="sticky-col">Código</th>}
                  {visibleStoneColumns.nombre && <th className="sticky-col">Nombre</th>}
                  {sizes.map(size => {
                    const sizeKey = size === 0 ? 'sin_tamano' : `size_${size}`;
                    // Si la columna no está definida, se muestra por defecto (true)
                    const isVisible = visibleStoneColumns[sizeKey] !== false;
                    return isVisible ? (
                      <th key={size} style={{ textAlign: 'center' }}>
                        {size === 0 ? 'Sin tamaño' : `${size}mm`}
                      </th>
                    ) : null;
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredStones.map((group) => (
                  <tr key={`${group.cod_piedra}-${group.nombre_piedra}`}>
                    {visibleStoneColumns.codigo && <td className="sticky-col"><strong>{group.cod_piedra}</strong></td>}
                    {visibleStoneColumns.nombre && <td className="sticky-col">{group.nombre_piedra}</td>}
                    {sizes.map(size => {
                      const sizeKey = size === 0 ? 'sin_tamano' : `size_${size}`;
                      // Si la columna no está definida, se muestra por defecto (true)
                      const isVisible = visibleStoneColumns[sizeKey] !== false;
                      if (!isVisible) return null;

                      const stockData = group.sizes.get(size);
                      if (!stockData) {
                        return (
                          <td key={size} style={{ textAlign: 'center' }} data-label={size === 0 ? 'Sin tamaño' : `${size}mm`}>
                          </td>
                        );
                      }
                      // Buscar el item de stock original para obtener el stone_id
                      const stockItem = stoneStock.find((item) => {
                        if (!item.stone) return false;
                        const cod_piedra = item.stone.cod_piedra;
                        const nombre_piedra = item.stone.nombre_piedra;
                        let size_mm: number | null = item.stone.size_mm ? parseFloat(item.stone.size_mm.toString()) : null;

                        if (!size_mm || isNaN(size_mm)) {
                          const codMatch = cod_piedra.match(/-(\d+)mm$/i);
                          if (codMatch) {
                            size_mm = parseInt(codMatch[1], 10);
                          } else {
                            const nombreMatch = nombre_piedra.match(/\s+(\d+)mm$/i);
                            if (nombreMatch) {
                              size_mm = parseInt(nombreMatch[1], 10);
                            }
                          }
                        }

                        const effectiveSize = (size_mm && !isNaN(size_mm) && size_mm > 0) ? Math.round(size_mm) : 0;
                        let baseCodPiedra = cod_piedra.replace(/-\d+mm$/i, '');
                        let baseNombrePiedra = nombre_piedra.replace(/\s+\d+mm$/i, '');

                        return baseCodPiedra === group.cod_piedra &&
                          baseNombrePiedra === group.nombre_piedra &&
                          effectiveSize === size;
                      });

                      // Si no encontramos el stockItem exacto, buscar cualquier piedra del grupo para obtener el ID
                      const fallbackStockItem = !stockItem ? stoneStock.find((item) => {
                        if (!item.stone) return false;
                        let baseCodPiedra = item.stone.cod_piedra.replace(/-\d+mm$/i, '');
                        let baseNombrePiedra = item.stone.nombre_piedra.replace(/\s+\d+mm$/i, '');
                        return baseCodPiedra === group.cod_piedra && baseNombrePiedra === group.nombre_piedra;
                      }) : null;

                      const finalStockItem = stockItem || fallbackStockItem;

                      return (
                        <td key={size} style={{ textAlign: 'center' }} data-label={size === 0 ? 'Sin tamaño' : `${size}mm`}>
                          {finalStockItem && finalStockItem.stone ? (
                            <StoneStockDisplay
                              stock={stockData.qty}
                              minStock={stockData.min_qty}
                              stoneId={finalStockItem.stone!.id}
                              stoneCode={finalStockItem.stone!.cod_piedra}
                              stoneName={finalStockItem.stone!.nombre_piedra}
                            />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                              <strong style={{
                                fontSize: '1.125rem',
                                color: getStockColor(stockData.qty, stockData.min_qty)
                              }}>
                                {stockData.qty}
                              </strong>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Resumen de Valor de Inventario de Piedras */}
        {!loading && stoneStock.length > 0 && isAdmin && (
          <div className="card" style={{
            marginTop: '2rem',
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'var(--bg-secondary)',
            border: 'none',
            boxShadow: 'none'
          }}>
            {(() => {
              const filteredStones = stoneStock.filter(item => {
                if (!item.stone) return false;

                // Filtro por tipo
                if (selectedStoneTypes.size > 0) {
                  const stoneType = getStoneType(item.stone.nombre_piedra, item.stone.cod_piedra);
                  if (!stoneType || !selectedStoneTypes.has(stoneType)) {
                    return false;
                  }
                }

                // Filtro por búsqueda
                if (!searchStone.trim()) return true;
                const search = searchStone.toLowerCase().trim();
                return (
                  item.stone.cod_piedra.toLowerCase().includes(search) ||
                  item.stone.nombre_piedra.toLowerCase().includes(search)
                );
              });

              const valuation = filteredStones.reduce((acc, item) => {
                if (!item.stone) return acc;

                const type = (item.stone.type || getStoneType(item.stone.nombre_piedra, item.stone.cod_piedra) || 'OTROS').toUpperCase();
                const cost = item.stone.coste_piedra || 0;
                const totalCost = item.qty * cost;

                if (type.includes('BEAD')) acc.beads += totalCost;
                else if (type.includes('DISC')) acc.discs += totalCost;
                else if (type.includes('PIPE')) acc.pipes += totalCost;
                else if (type.includes('SPACER')) acc.spacers += totalCost;
                else acc.others += totalCost;

                acc.total += totalCost;
                return acc;
              }, { beads: 0, discs: 0, pipes: 0, spacers: 0, others: 0, total: 0 });

              const formatCurrency = (amount: number) =>
                new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);

              return (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Beads</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {formatCurrency(valuation.beads)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Discs</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {formatCurrency(valuation.discs)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Pipes</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {formatCurrency(valuation.pipes)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Spacers</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {formatCurrency(valuation.spacers)}
                    </span>
                  </div>
                  {valuation.others > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Otros</span>
                      <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {formatCurrency(valuation.others)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', borderLeft: '2px solid var(--border)', paddingLeft: '1.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Valor Total Piedras</span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)' }}>{formatCurrency(valuation.total)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Modal de configuración de columnas - Piedras */}
      <Modal
        isOpen={isStoneColumnsModalOpen}
        onClose={() => setIsStoneColumnsModalOpen(false)}
        title="Personalizar Columnas - Piedras"
        footer={
          <button className="btn btn-primary" onClick={() => setIsStoneColumnsModalOpen(false)}>
            Cerrar
          </button>
        }
      >
        {(() => {
          // Obtener los tamaños disponibles dinámicamente
          const allSizes = new Set<number>();
          stoneStock.forEach((item) => {
            if (!item.stone) return;
            const size_mm_raw = item.stone.size_mm;
            let size_mm: number | null = size_mm_raw ? parseFloat(size_mm_raw.toString()) : null;
            if (!size_mm || isNaN(size_mm)) {
              const codMatch = item.stone.cod_piedra.match(/-(\d+)mm$/i);
              if (codMatch) {
                size_mm = parseInt(codMatch[1], 10);
              } else {
                const nombreMatch = item.stone.nombre_piedra.match(/\s+(\d+)mm$/i);
                if (nombreMatch) {
                  size_mm = parseInt(nombreMatch[1], 10);
                }
              }
            }
            if (size_mm && !isNaN(size_mm) && size_mm > 0) {
              allSizes.add(Math.round(size_mm));
            }
          });
          const standardSizes = [4, 6, 8, 10, 12];
          const otherSizes = Array.from(allSizes).filter(s => !standardSizes.includes(s)).sort((a, b) => a - b);
          const allSizesArray = [...standardSizes, ...otherSizes];

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={visibleStoneColumns.codigo ?? true}
                    onChange={() => handleToggleStoneColumn('codigo')}
                  />
                  <span>Código</span>
                </label>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={visibleStoneColumns.nombre ?? true}
                    onChange={() => handleToggleStoneColumn('nombre')}
                  />
                  <span>Nombre</span>
                </label>
              </div>
              <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)' }}>
                <strong style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Tamaños:</strong>
              </div>
              {allSizesArray.map(size => {
                const sizeKey = `size_${size}`;
                return (
                  <div key={size}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={visibleStoneColumns[sizeKey] !== false}
                        onChange={() => handleToggleStoneColumn(sizeKey)}
                      />
                      <span>{size}mm</span>
                    </label>
                  </div>
                );
              })}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={visibleStoneColumns.sin_tamano !== false}
                    onChange={() => handleToggleStoneColumn('sin_tamano')}
                  />
                  <span>Sin tamaño</span>
                </label>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Modal Editar Stock */}
      <Modal
        isOpen={isEditStockModalOpen}
        onClose={() => {
          setIsEditStockModalOpen(false);
          setEditingStock(null);
          setEditStockForm({ new_qty: '' });
        }}
        title="Ajustar Stock Manualmente"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsEditStockModalOpen(false);
                setEditingStock(null);
                setEditStockForm({ new_qty: '' });
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveStock}
              data-modal-default="true"
            >
              Guardar
            </button>
          </>
        }
      >
        {editingStock && (
          <>
            <div className="input-group">
              <label className="input-label">Piedra</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong>{editingStock.stoneCode}</strong> - {editingStock.stoneName}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Ubicación</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                {editingStock.locationName}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Stock Actual</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong>{editingStock.currentQty}</strong> unidades
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Nueva Cantidad *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input"
                value={editStockForm.new_qty}
                onChange={(e) => setEditStockForm({ ...editStockForm, new_qty: e.target.value })}
                placeholder={`Actual: ${editingStock.currentQty}`}
                required
                autoFocus
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                Establece la cantidad exacta de stock que deseas tener en esta ubicación
              </small>
            </div>

            {!isAdmin && (
              <div style={{ padding: '1rem', backgroundColor: 'rgba(255, 59, 48, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 59, 48, 0.3)' }}>
                <strong style={{ color: 'var(--error)' }}>Advertencia:</strong>
                <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                  Solo los administradores pueden ajustar stock manualmente.
                </p>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Modal Editar Mínimo */}
      <Modal
        isOpen={isEditMinStockModalOpen}
        onClose={() => {
          setIsEditMinStockModalOpen(false);
          setEditingMinStock(null);
          setEditMinStockForm({ new_min: '' });
        }}
        title="Editar Stock Mínimo"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsEditMinStockModalOpen(false);
                setEditingMinStock(null);
                setEditMinStockForm({ new_min: '' });
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveMinStock}
              data-modal-default="true"
            >
              Guardar
            </button>
          </>
        }
      >
        {editingMinStock && (
          <>
            <div className="input-group">
              <label className="input-label">Piedra</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong>{editingMinStock.stoneCode}</strong> - {editingMinStock.stoneName}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Ubicación</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                Global / Almacén
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Mínimo actual</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong>{editingMinStock.currentMin}</strong> unidades
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Nuevo mínimo *</label>
              <input
                type="number"
                min="0"
                step="1"
                className="input"
                value={editMinStockForm.new_min}
                onChange={(e) => setEditMinStockForm({ ...editMinStockForm, new_min: e.target.value })}
                placeholder={`Actual: ${editingMinStock.currentMin}`}
                required
                autoFocus
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                Aplica al stock mínimo global de la piedra
              </small>
            </div>

            {!isAdmin && (
              <div style={{ padding: '1rem', backgroundColor: 'rgba(255, 59, 48, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 59, 48, 0.3)' }}>
                <strong style={{ color: 'var(--error)' }}>Advertencia:</strong>
                <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                  Solo los administradores pueden modificar mínimos.
                </p>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Modal de Impresión */}
      <Modal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        title="Imprimir Lista de Stock"
        footer={
          <button className="btn btn-secondary" onClick={() => setIsPrintModalOpen(false)}>
            Cancelar
          </button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            Selecciona el tipo de impresión que deseas:
          </p>
          <button
            className="btn btn-primary"
            onClick={() => handlePrintStock(true)}
            style={{ width: '100%', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <Printer size={20} />
            Imprimir con Cantidades
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handlePrintStock(false)}
            style={{ width: '100%', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <Printer size={20} />
            Imprimir Plantilla Vacía
          </button>
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            <strong>Nota:</strong> Se imprimirán todas las piedras de la lista completa {selectedStoneTypes.size > 0 ? `(filtradas por tipos: ${Array.from(selectedStoneTypes).join(', ')})` : '(sin filtros)'} con las columnas visibles actuales.
          </div>
        </div>
      </Modal>
    </div>
  );
}

